<template>
  <div :class="isMobile ? 'modal mobile-modal' : 'modal-overlay'">
    <div class="modal">
      <div class="modal-header">
        <h3>{{ readOnly ? spot.name : 'Add a Spot' }}</h3>
        <button type="button" class="close-btn" aria-label="Close" @click="$emit('close')">×</button>
      </div>

      <template v-if="readOnly">
        <p v-if="currentNotes" class="notes">{{ currentNotes }}</p>
        <p v-else class="notes-empty">No description yet — add one below.</p>
        <div class="tag-list">
          <span v-for="tag in currentTags" :key="tag" class="tag-pill">{{ tag }}</span>
        </div>
        <p class="created-at">Added {{ formattedDate }}</p>

        <div class="add-note">
          <p class="add-note-label">Add to the description</p>
          <textarea
            v-model="noteAddition"
            placeholder="Add more info about this spot..."
            maxlength="500"
          ></textarea>
          <p v-if="addNoteError" class="error">{{ addNoteError }}</p>
          <button @click="submitNote" :disabled="addingNote">Add</button>
        </div>

        <div class="add-note" v-if="addableTags.length > 0">
          <p class="add-note-label">Add tags</p>
          <div class="tag-list">
            <button
              v-for="tag in addableTags"
              :key="tag"
              type="button"
              :class="['tag-pill', 'selectable', { active: pendingTags.includes(tag) }]"
              @click="togglePendingTag(tag)"
            >
              {{ tag }}
            </button>
          </div>
          <p v-if="addTagsError" class="error">{{ addTagsError }}</p>
          <button @click="submitTags" :disabled="pendingTags.length === 0 || addingTags">Add Tags</button>
        </div>

        <button @click="$emit('close')">Close</button>
      </template>

      <template v-else>
        <!-- Honeypot: invisible to real users, tabindex/autocomplete off so they can't
             tab into it or have it autofilled. A bot that fills every field gives itself
             away by populating this one. Backend silently drops the submission if set. -->
        <div class="hp-field" aria-hidden="true">
          <label for="website">Website</label>
          <input id="website" v-model="website" name="website" type="text" tabindex="-1" autocomplete="off" />
        </div>

        <input v-model="name" placeholder="Spot name" maxlength="100" />
        <textarea v-model="notes" placeholder="Notes (optional)" maxlength="2000"></textarea>

        <p class="tags-label">Tags</p>
        <div class="tag-list">
          <button
            v-for="tag in availableTags"
            :key="tag"
            type="button"
            :class="['tag-pill', 'selectable', { active: selectedTags.includes(tag) }]"
            @click="toggleTag(tag)"
          >
            {{ tag }}
          </button>
        </div>

        <p v-if="error" class="error">{{ error }}</p>

        <button @click="save">Save Spot</button>
        <button @click="$emit('cancel')">Cancel</button>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { AVAILABLE_TAGS } from '../tags';

export default {
  name: 'SpotEditorModal',
  props: {
    isMobile: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    spot: { type: Object, default: () => ({ name: '', notes: '', tags: [], createdAt: null }) },
  },
  emits: ['save', 'cancel', 'close', 'spot-updated'],
  setup(props, { emit }) {
    const name = ref('');
    const notes = ref('');
    const website = ref('');
    const selectedTags = ref([]);
    const error = ref('');

    // Mirrors props.spot.notes but updates immediately on a successful add, so the reader
    // sees their own addition without waiting on the parent to refetch everything.
    const currentNotes = ref(props.spot.notes || null);
    const noteAddition = ref('');
    const addNoteError = ref('');
    const addingNote = ref(false);

    const submitNote = async () => {
      const text = noteAddition.value.trim();
      if (!text) {
        addNoteError.value = 'Enter something to add first.';
        return;
      }
      addingNote.value = true;
      addNoteError.value = '';
      try {
        const response = await fetch(`/api/spots/${props.spot.id}/notes`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ addition: text }),
        });
        if (!response.ok) {
          addNoteError.value = response.status === 429
            ? "You're adding too fast — try again in a bit."
            : await response.text();
          return;
        }
        const updated = await response.json();
        currentNotes.value = updated.notes;
        noteAddition.value = '';
        emit('spot-updated', updated);
      } catch (e) {
        addNoteError.value = 'Error adding note.';
      } finally {
        addingNote.value = false;
      }
    };

    // Same append-only philosophy as notes: anyone can add missing tags, nobody can
    // remove ones that are already there (see AddTags in SpotsController.cs for why).
    const currentTags = ref([...(props.spot.tags || [])]);
    const pendingTags = ref([]);
    const addTagsError = ref('');
    const addingTags = ref(false);

    const addableTags = computed(() =>
      AVAILABLE_TAGS.filter((tag) => !currentTags.value.some((t) => t.toLowerCase() === tag.toLowerCase()))
    );

    const togglePendingTag = (tag) => {
      pendingTags.value = pendingTags.value.includes(tag)
        ? pendingTags.value.filter((t) => t !== tag)
        : [...pendingTags.value, tag];
    };

    const submitTags = async () => {
      if (pendingTags.value.length === 0) return;
      addingTags.value = true;
      addTagsError.value = '';
      try {
        const response = await fetch(`/api/spots/${props.spot.id}/tags`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: pendingTags.value }),
        });
        if (!response.ok) {
          addTagsError.value = response.status === 429
            ? "You're adding too fast — try again in a bit."
            : await response.text();
          return;
        }
        const updated = await response.json();
        currentTags.value = updated.tags;
        pendingTags.value = [];
        emit('spot-updated', updated);
      } catch (e) {
        addTagsError.value = 'Error adding tags.';
      } finally {
        addingTags.value = false;
      }
    };

    // The modal stays mounted when switching directly from one existing spot to another
    // (MapComponent just reassigns activeSpot rather than toggling it off and on), so
    // setup() only runs once — without this, currentNotes/currentTags/pendingTags etc.
    // would keep showing whatever spot was open *first* instead of the one now displayed.
    watch(
      () => props.spot,
      (newSpot) => {
        currentNotes.value = newSpot.notes || null;
        currentTags.value = [...(newSpot.tags || [])];
        noteAddition.value = '';
        addNoteError.value = '';
        pendingTags.value = [];
        addTagsError.value = '';
      }
    );

    const toggleTag = (tag) => {
      selectedTags.value = selectedTags.value.includes(tag)
        ? selectedTags.value.filter((t) => t !== tag)
        : [...selectedTags.value, tag];
    };

    const save = () => {
      if (!name.value.trim()) {
        error.value = 'Name is required.';
        return;
      }
      error.value = '';
      emit('save', {
        name: name.value.trim(),
        notes: notes.value.trim(),
        tags: selectedTags.value,
        website: website.value,
      });
    };

    const formattedDate = computed(() =>
      props.spot.createdAt ? new Date(props.spot.createdAt).toLocaleString() : ''
    );

    return {
      name,
      notes,
      website,
      selectedTags,
      error,
      availableTags: AVAILABLE_TAGS,
      toggleTag,
      save,
      formattedDate,
      currentNotes,
      noteAddition,
      addNoteError,
      addingNote,
      submitNote,
      currentTags,
      pendingTags,
      addTagsError,
      addingTags,
      addableTags,
      togglePendingTag,
      submitTags,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: 320px;
  height: 100%;
  background-color: white;
  z-index: 1001;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
}

.modal {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  width: 280px;
}

.modal.mobile-modal {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  max-height: 70vh;
  overflow-y: auto;
  padding: 10px;
  background-color: #fff;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.2);
  border-radius: 15px 15px 0 0;
  z-index: 1001;
  box-sizing: border-box;
}

.modal.mobile-modal .modal {
  width: 100%;
  box-sizing: border-box;
}

/* Sticky so it's still reachable without scrolling through notes/tags/add-note content
   first — the whole point of adding it (see the mobile "have to scroll to the bottom to
   close" complaint this was added for). Negative margin/top cancels out .modal's own
   padding so it sits flush at the scroll container's edge instead of leaving a gap. */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  position: sticky;
  top: -20px;
  margin: -20px -20px 10px;
  padding: 16px 20px 8px;
  background: white;
  z-index: 2;
}

.modal-header h3 {
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
}

.close-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  border-radius: 50%;
  background: white;
  font-size: 20px;
  line-height: 1;
  color: #555;
  cursor: pointer;
}

input,
textarea {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 10px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

textarea {
  min-height: 70px;
  resize: vertical;
}

button {
  margin-right: 8px;
  margin-bottom: 8px;
  padding: 8px 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.tags-label {
  font-size: 12px;
  color: #666;
  margin: 4px 0;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.tag-pill {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  background: #eee;
  border: 1px solid #ddd;
}

.tag-pill.selectable {
  cursor: pointer;
}

.tag-pill.active {
  background-color: #2d7a3a;
  border-color: #2d7a3a;
  color: white;
}

.notes {
  white-space: pre-wrap;
}

.created-at {
  font-size: 12px;
  color: #888;
}

.notes-empty {
  font-style: italic;
  color: #888;
  font-size: 13px;
}

.add-note {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.add-note-label {
  font-size: 12px;
  color: #666;
  margin: 0 0 6px;
}

.add-note textarea {
  min-height: 50px;
}

.error {
  color: #a33;
  font-size: 13px;
}

.hp-field {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
</style>
